import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Flex, Grid, Input } from "antd";
import { EditOutlined } from "@ant-design/icons";
import TriggerRulesList, { type WorkflowRecord } from "./TriggerRulesList";
import TriggerRulesEditor from "./TriggerRulesEditor";
import { TriggerTypeSelectionPanel } from "./components/TriggerTypeSelectionPanel";
import { useWorkflow, useWorkflows } from "./hooks/useWorkflows";
import HButton from "../../components/custom/input/button/Button";
import { Trigger, type TriggerType } from "./nodes/TriggerNode";
import "./triggerRules.css";

const TriggerRules: React.FC = () => {
    const { ruleId: idFromUrl } = useParams<{ ruleId?: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowRecord | null | "new">(null);
    const [triggerTypeForNew, setTriggerTypeForNew] = useState<TriggerType | null>(null);
    const { md: greaterThanMediumScreen } = Grid.useBreakpoint();

    const workflows = useWorkflows();

    const activeWorkflowId = selectedWorkflow !== null && selectedWorkflow !== "new"
        ? selectedWorkflow.workflowId
        : null;
    const { workflow: serverWorkflow, isLoading: workflowLoading, error: workflowLoadError } =
        useWorkflow(activeWorkflowId);

    const serverWorkflowStale =
        activeWorkflowId != null &&
        serverWorkflow != null &&
        serverWorkflow.workflowId !== activeWorkflowId;

    const awaitingServerWorkflow =
        activeWorkflowId != null &&
        (workflowLoading ||
            serverWorkflowStale ||
            (serverWorkflow === null && workflowLoadError == null));

    useEffect(() => {
        if (!idFromUrl) {
            setSelectedWorkflow(null);
            setTriggerTypeForNew(null);
            return;
        }
        if (idFromUrl === "new") {
            setSelectedWorkflow("new");
            return;
        }
        setSelectedWorkflow((prev) => {
            if (typeof prev === "object" && prev !== null && prev.workflowId === idFromUrl) {
                return prev;
            }
            return {
                workflowId: idFromUrl,
                workflowName: "",
                status: "",
                triggerType: Trigger.Message.Inbound,
                direction: "Incoming",
                runs: 0,
            };
        });
    }, [idFromUrl]);

    const handleWorkflowClick = (workflow: WorkflowRecord) => {
        setSelectedWorkflow(workflow);
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules/${workflow.workflowId}${location.search}`);
    };

    const handleNewWorkflow = () => {
        setSelectedWorkflow("new");
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules/new${location.search}`);
    };

    const handleBack = () => {
        setSelectedWorkflow(null);
        setTriggerTypeForNew(null);
        navigate(`/messaging/automation/trigger-rules${location.search}`);
    };

    const getTriggerType = (): TriggerType => {
        if (selectedWorkflow !== null && selectedWorkflow !== "new") {
            return selectedWorkflow.direction === "Outgoing" ? Trigger.Message.Outbound : Trigger.Message.Inbound;
        }
        return triggerTypeForNew ?? Trigger.Message.Inbound;
    };

    const handleWorkflowSaved = (newWorkflowId: string): void => {
        const triggerType = getTriggerType();
        setSelectedWorkflow({
            workflowId: newWorkflowId,
            workflowName: "",
            status: "",
            triggerType,
            direction: (triggerType === Trigger.Message.Outbound ? "Outgoing" : "Incoming") as "Incoming" | "Outgoing",
            runs: 0,
        });
        navigate(`/messaging/automation/trigger-rules/${newWorkflowId}${location.search}`);
    };

    const handleTriggerTypeConfirm = (triggerType: TriggerType): void => {
        setTriggerTypeForNew(triggerType);
    };

    if (selectedWorkflow === null) {
        return (
            <TriggerRulesList
                workflows={workflows}
                onWorkflowClick={handleWorkflowClick}
                onNewWorkflow={handleNewWorkflow}
            />
        );
    }

    if (selectedWorkflow === "new" && triggerTypeForNew === null) {
        return (
            <Flex className="trigger-rules hs-min-h-0" vertical={!greaterThanMediumScreen}>
                <div
                    className={
                        greaterThanMediumScreen
                            ? "d-flex flex-column flex-fill hs-min-h-0"
                            : "d-flex flex-column flex-shrink-0"
                    }
                >
                    <Flex
                        className="py-3 px-3 hs-border-bottom-light"
                        vertical={!greaterThanMediumScreen}
                        gap={greaterThanMediumScreen ? 0 : 16}
                        justify={greaterThanMediumScreen ? "space-between" : undefined}
                        align={greaterThanMediumScreen ? "center" : "stretch"}
                    >
                        <Flex align="center" gap={12} style={{ minWidth: 0, width: greaterThanMediumScreen ? "auto" : "100%" }}>
                            <Input
                                value="New Rule"
                                disabled
                                suffix={<EditOutlined aria-hidden />}
                                aria-label="Rule name"
                                style={{ width: greaterThanMediumScreen ? undefined : "100%" }}
                            />
                        </Flex>
                        {greaterThanMediumScreen ? (
                            <Flex gap={12} wrap="wrap">
                                <HButton
                                    size="s"
                                    type="text"
                                    buttonClassName="hs-color-BlackRussian"
                                    onClick={handleBack}
                                >
                                    Cancel
                                </HButton>
                                <HButton size="s" type="primary" disabled>
                                    Save
                                </HButton>
                            </Flex>
                        ) : (
                            <Flex gap={12} style={{ width: "100%" }}>
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <HButton
                                        size="s"
                                        type="text"
                                        buttonClassName="hs-color-BlackRussian w-100"
                                        onClick={handleBack}
                                    >
                                        Cancel
                                    </HButton>
                                </div>
                                <div className="flex-fill" style={{ minWidth: 0 }}>
                                    <HButton size="s" type="primary" disabled buttonClassName="w-100">
                                        Save
                                    </HButton>
                                </div>
                            </Flex>
                        )}
                    </Flex>
                    {greaterThanMediumScreen ? (
                        <div className="d-flex flex-fill hs-min-h-0">
                            <div className="position-relative hs-bg-light-grey-1 d-flex justify-content-center align-items-center flex-fill">
                                <div
                                    className="automationCardsSprite trigger-rules-trigger-center-illustration"
                                    aria-hidden
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
                <div
                    className={
                        greaterThanMediumScreen
                            ? "hs-w-400-px hs-border-left-light"
                            : "hs-border-top-light d-flex flex-column flex-fill hs-min-h-0"
                    }
                    style={{ width: greaterThanMediumScreen ? undefined : "100%", minWidth: 0 }}
                >
                    <TriggerTypeSelectionPanel
                        onConfirm={handleTriggerTypeConfirm}
                        onCancel={handleBack}
                        isCompact={!greaterThanMediumScreen}
                    />
                </div>
            </Flex>
        );
    }

    if (awaitingServerWorkflow) {
        return (
            <div className="d-flex justify-content-center align-items-center trigger-rules-editor-loading-overlay">
                <div className="actionIconsSprite dropDownLoading" />
            </div>
        );
    }

    return (
        <TriggerRulesEditor
            workflow={selectedWorkflow === "new" ? null : selectedWorkflow}
            serverWorkflow={serverWorkflow}
            onBack={handleBack}
            onWorkflowSaved={handleWorkflowSaved}
            triggerType={getTriggerType()}
        />
    );
};

export default TriggerRules;
